<template>
  <app-dialog
    v-model="showPDFEditorDialog"
    :transition="false"
    content-class="sourcery__dialog--fullscreen"
    fullscreen
    origin="right"
    persistent>
    <v-card>
      <div class="app-pdf-editor">
        <div
          ref="pdfContainer"
          class="app-pdf-editor__container" />
      </div>
    </v-card>
  </app-dialog>
</template>

<script>
import API from '@/services/graphql';
import { parseStorageKey } from '@/utils';
import { loadEmbedPDF } from '@/utils/embedpdf';
import { mapMutations } from 'vuex';
import {
  EventBus,
  REFRESH_VALUE_URLS,
} from '@/utils/eventBus';

export default {
  name: 'AppPDFEditor',
  props: {
    src: {
      type: String,
      required: true,
    },
    /** The existing attachment id — used to overwrite the same S3 key on save. */
    attachmentId: {
      type: String,
      required: true,
    },
    /** Filename to use when uploading the edited PDF. Falls back to the last path segment of src. */
    filename: {
      type: String,
      default: '',
    },
    /** The raw storage key from the attachment object (e.g. "protected/identityId/attachmentId/file.pdf"). */
    storageKey: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      showPDFEditorDialog: true,
      viewerElement: null,
      scrollPlugin: null,
      scrollUnsubscribe: null,
      pdfBytes: null,
      pageCount: 0,
      activePageIndex: 0,
      loading: false,
      saving: false,
      errorMessage: '',
    };
  },

  created() {
    // Non-reactive: only used internally for blob lifecycle management
    this._currentBlobUrl = null;
    this._currentDocumentId = null;
    this._docManager = null;
    this._pendingScrollPageNumber = null;
    this._pendingSidebarState = null;
    this._ui = null;
  },

  async mounted() {
    await this.initPdfViewer();
  },

  beforeDestroy() {
    this.unsubscribeScroll();
    this.cleanupBlobUrl();
    const container = this.$refs.pdfContainer;
    if (container) {
      container.innerHTML = '';
    }
    this.viewerElement = null;
  },

  methods: {
    ...mapMutations(['spinner']),

    /** Add a blank page at the end. */
    async addPageAtEnd() {
      this.saving = true;
      this.errorMessage = '';
      try {
        await this.syncBytesFromViewer();
        const doc = await this.createWorkingDoc();
        const { width, height } = doc.getPages()[0].getSize();
        doc.addPage([width, height]);
        this.pdfBytes = await doc.save();
        this.pageCount = doc.getPageCount();
        this.activePageIndex = this.pageCount - 1;
        await this.refreshViewerFromBytes();
      } catch (err) {
        this.errorMessage = err.message || 'Failed to add page';
        this.$emit('error', this.errorMessage);
      } finally {
        this.saving = false;
      }
    },

    /**
     * Reorder pages by copying them into a new document in the given order.
     * @param {number[]} newOrderIndices - 0-based page indices in desired order (e.g. [0, 2, 1, 3]).
     * @param {number} [optionalTargetPageIndex] - 0-based page index to show after reorder (for move up/down).
     */
    async applyReorder(newOrderIndices, optionalTargetPageIndex) {
      if (!this.pdfBytes || this.pdfBytes.length === 0 || !newOrderIndices.length) {
        return;
      }
      await this.syncBytesFromViewer();
      const { PDFDocument } = await import('pdf-lib');
      const sourceDoc = await this.createWorkingDoc();
      const n = sourceDoc.getPageCount();
      const valid = newOrderIndices.length === n
        && newOrderIndices.every((i) => Number.isInteger(i) && i >= 0 && i < n);
      if (!valid) {
        this.errorMessage = 'Invalid page order for reorder';
        this.$emit('error', this.errorMessage);
        return;
      }
      const newDoc = await PDFDocument.create();
      const copiedPages = await newDoc.copyPages(sourceDoc, newOrderIndices);
      copiedPages.forEach((page) => newDoc.addPage(page));
      this.pdfBytes = await newDoc.save();
      this.pageCount = newDoc.getPageCount();
      const options = optionalTargetPageIndex !== undefined
        ? {
          targetPageNumber: optionalTargetPageIndex + 1,
        }
        : undefined;
      await this.refreshViewerFromBytes(options);
    },

    /** Revoke current blob URL to avoid memory leaks. */
    cleanupBlobUrl() {
      if (this._currentBlobUrl) {
        URL.revokeObjectURL(this._currentBlobUrl);
        this._currentBlobUrl = null;
      }
    },

    /**
     * Discover (placement, slot) for sidebars from UI schema. Uses first sidebar found, or ('left', 'main').
     */
    getSidebarPlacementAndSlot() {
      try {
        const schema = this._ui?.getSchema?.();
        const sidebars = schema?.sidebars;
        if (sidebars && typeof sidebars === 'object') {
          const first = Object.values(sidebars)[0];
          if (first?.position) {
            return {
              placement: first.position.placement || 'left',
              slot: first.position.slot || 'main',
            };
          }
        }
      } catch (_) {
        // ignore
      }
      return {
        placement: 'left',
        slot: 'main',
      };
    },

    /**
     * Capture active sidebar state via ui.forDocument().getActiveSidebar(placement, slot).
     * Returns { placement, slot, sidebarId, activeTab? } if a sidebar is open, null otherwise.
     * See embedpdf-ui-sidebar-api.md for API details.
     */
    captureSidebarOpenState() {
      if (!this._ui || this._currentDocumentId == null) return null;
      try {
        const docUi = this._ui.forDocument(this._currentDocumentId);
        if (!docUi || typeof docUi.getActiveSidebar !== 'function') return null;
        const { placement, slot } = this.getSidebarPlacementAndSlot();
        const sidebarId = docUi.getActiveSidebar(placement, slot);
        if (sidebarId == null || sidebarId === '') return null;
        const activeTab = typeof docUi.getSidebarTab === 'function'
          ? docUi.getSidebarTab(sidebarId)
          : undefined;
        return {
          placement,
          slot,
          sidebarId,
          activeTab: activeTab ?? undefined,
        };
      } catch (_) {
        return null;
      }
    },

    /**
     * Restore sidebar state via ui.forDocument().setActiveSidebar(placement, slot, sidebarId, activeTab).
     */
    restoreSidebarOpenState(documentId) {
      const state = this._pendingSidebarState;
      if (!this._ui || !state || documentId == null) return;
      try {
        const docUi = this._ui.forDocument(documentId);
        if (typeof docUi.setActiveSidebar !== 'function') return;
        docUi.setActiveSidebar(
          state.placement,
          state.slot,
          state.sidebarId,
          state.activeTab,
        );
      } catch (_) {
        // UI plugin shape may vary by EmbedPDF version
      } finally {
        this._pendingSidebarState = null;
      }
    },

    closePDFEditor() {
      EventBus.$emit(REFRESH_VALUE_URLS);
      this.showPDFEditorDialog = false;
      this.$emit('close');
    },

    /** Create a working PDFDocument from current bytes (pdf-lib). */
    async createWorkingDoc() {
      if (!this.pdfBytes || this.pdfBytes.length === 0) {
        throw new Error('No PDF bytes loaded');
      }
      const { PDFDocument } = await import('pdf-lib');
      return PDFDocument.load(this.pdfBytes);
    },

    /**
     * Export the current viewer state (including any annotations the user has
     * added) as a Uint8Array. Falls back to this.pdfBytes when the viewer is
     * not yet initialised (e.g. during the first load before mount completes).
     */
    async exportViewerBytes() {
      if (!this.viewerElement) return this.pdfBytes;
      try {
        const registry = await this.viewerElement.registry;
        const exportPlugin = registry.getPlugin('export').provides();
        const arrayBuffer = await exportPlugin.saveAsCopy().toPromise();
        return new Uint8Array(arrayBuffer);
      } catch (err) {
        console.warn('[AppPDFEditor] exportViewerBytes failed, falling back to pdfBytes:', err);
        return this.pdfBytes;
      }
    },

    async initPdfViewer() {
      try {
        const bytes = await this.loadPdfBytes();
        if (!bytes || bytes.length === 0) return;

        this.activePageIndex = 0;
        await this.refreshViewerFromBytes();
        // pageCount is set by the scroll plugin's onPageChange once the viewer mounts
      } catch (_) {
        // errorMessage already set and emitted in loadPdfBytes
      }
    },

    /** Insert a blank page after the current page. */
    async insertPageAfterCurrent() {
      this.saving = true;
      this.errorMessage = '';
      try {
        await this.syncBytesFromViewer();
        const doc = await this.createWorkingDoc();
        const { width, height } = doc.getPages()[this.activePageIndex].getSize();
        const insertIndex = this.activePageIndex + 1;
        doc.insertPage(insertIndex, [width, height]);
        this.pdfBytes = await doc.save();
        this.pageCount = doc.getPageCount();
        this.activePageIndex = insertIndex;
        await this.refreshViewerFromBytes();
      } catch (err) {
        this.errorMessage = err.message || 'Failed to insert page';
        this.$emit('error', this.errorMessage);
      } finally {
        this.saving = false;
      }
    },

    /** Load PDF from the src prop into memory (bytes). */
    async loadPdfBytes() {
      this.loading = true;
      this.errorMessage = '';
      try {
        const response = await fetch(this.src);
        if (!response.ok) {
          throw new Error(`Failed to load PDF: ${response.status}`);
        }
        const arrayBuffer = await response.arrayBuffer();
        this.pdfBytes = new Uint8Array(arrayBuffer);
        return this.pdfBytes;
      } catch (err) {
        this.errorMessage = err.message || 'Failed to load PDF';
        this.$emit('error', this.errorMessage);
        throw err;
      } finally {
        this.loading = false;
      }
    },

    /** Mount viewer with URL or re-mount with new URL so embed-pdf shows updated doc. */
    async mountOrUpdateViewer(url) {
      const container = this.$refs.pdfContainer;
      if (!container) return;

      this.unsubscribeScroll();
      container.innerHTML = '';
      this.viewerElement = null;
      this._docManager = null;
      this._currentDocumentId = null;
      this._pendingScrollPageNumber = null;
      this._pendingSidebarState = null;
      this._ui = null;

      const EmbedPDF = await loadEmbedPDF();
      const viewerElement = EmbedPDF.init({
        type: 'container',
        target: container,
        src: url,
        disabledCategories: ['document-close', 'document-protect'],
      });
      if (!viewerElement) return;
      this.viewerElement = viewerElement;

      const vm = this;
      viewerElement.registry.then((registry) => {
        if (!vm.viewerElement || vm.viewerElement !== viewerElement) return;
        const scroll = registry.getPlugin('scroll').provides();
        vm.scrollPlugin = scroll;
        vm.scrollUnsubscribe = scroll.onPageChange((event) => {
          vm.activePageIndex = event.pageNumber - 1;
          vm.pageCount = event.totalPages;
          vm.$emit('page-change', {
            pageIndex: vm.activePageIndex,
            pageCount: vm.pageCount,
          });
        });

        // Use document-manager to update document in-place on reorder (avoids full viewer teardown)
        try {
          const docManager = registry.getPlugin('document-manager').provides();
          vm._docManager = docManager;
          docManager.onDocumentOpened((doc) => {
            if (!vm.viewerElement) return;
            vm._currentDocumentId = doc.id;
            if (vm._pendingScrollPageNumber != null && vm._ui) {
              vm._ui.setActiveToolbar('top', 'secondary', 'reorder-toolbar', doc.id);
            }
            if (vm._pendingSidebarState) {
              vm.restoreSidebarOpenState(doc.id);
            }
            vm._pendingSidebarState = null;
          });
        } catch (_) {
          // document-manager not available; refreshViewerFromBytes will fall back to full re-mount
        }

        const commands = registry.getPlugin('commands').provides();
        const ui = registry.getPlugin('ui').provides();
        vm._ui = ui;

        scroll.onLayoutReady((event) => {
          if (!vm.viewerElement || vm._pendingScrollPageNumber == null) return;
          if (event.documentId !== vm._currentDocumentId) return;
          scroll.scrollToPage({
            pageNumber: vm._pendingScrollPageNumber,
            behavior: 'instant',
          });
          vm._pendingScrollPageNumber = null;
        });

        const reorderCommandIds = {
          addPage: 'custom.reorder.add-page',
          insertAfter: 'custom.reorder.insert-after',
          removeCurrent: 'custom.reorder.remove-current',
          moveUp: 'custom.reorder.move-up',
          moveDown: 'custom.reorder.move-down',
        };

        commands.registerCommand({
          id: reorderCommandIds.addPage,
          label: 'Add page (end)',
          action: () => vm.addPageAtEnd(),
          disabled: () => vm.loading || vm.saving,
        });
        commands.registerCommand({
          id: reorderCommandIds.insertAfter,
          label: 'Insert after current',
          action: () => vm.insertPageAfterCurrent(),
          disabled: () => !vm.pageCount || vm.loading || vm.saving,
        });
        commands.registerCommand({
          id: reorderCommandIds.removeCurrent,
          label: 'Remove current page',
          action: () => vm.removeCurrentPage(),
          disabled: () => !vm.pageCount || vm.loading || vm.saving || vm.pageCount <= 1,
        });
        commands.registerCommand({
          id: reorderCommandIds.moveUp,
          label: 'Move up',
          action: () => vm.movePageUp(),
          disabled: () => !vm.pageCount || vm.loading || vm.saving || vm.activePageIndex <= 0,
        });
        commands.registerCommand({
          id: reorderCommandIds.moveDown,
          label: 'Move down',
          action: () => vm.movePageDown(),
          disabled: () => !vm.pageCount || vm.loading || vm.saving || vm.activePageIndex >= vm.pageCount - 1,
        });

        // Register mode command so the "Reorder" tab activates the reorder secondary toolbar.
        commands.registerCommand({
          id: 'mode:reorder',
          label: 'Reorder/Delete',
          action: ({ documentId }) => {
            ui.setActiveToolbar('top', 'secondary', 'reorder-toolbar', documentId);
          },
          categories: ['mode', 'mode-reorder'],
        });

        commands.registerCommand({
          id: 'custom.save-and-close',
          label: 'Save and Close',
          action: () => vm.saveAndClose(),
          disabled: () => vm.loading || vm.saving,
        });

        const schema = ui.getSchema();
        // Add new toolbar category "reorder-toolbar"
        const reorderToolbar = {
          id: 'reorder-toolbar',
          position: {
            placement: 'top',
            slot: 'secondary',
            order: 0,
          },
          permanent: false,
          categories: ['reorder'],
          items: [
            {
              type: 'spacer',
              id: 'reorder-spacer-start',
              flex: true,
            },
            {
              type: 'group',
              id: 'reorder-tools',
              alignment: 'start',
              gap: 2,
              items: [
                {
                  type: 'command-button',
                  id: 'reorder-add-page',
                  commandId: reorderCommandIds.addPage,
                  variant: 'text',
                  categories: ['reorder'],
                },
                {
                  type: 'command-button',
                  id: 'reorder-insert-after',
                  commandId: reorderCommandIds.insertAfter,
                  variant: 'text',
                  categories: ['reorder'],
                },
                {
                  type: 'command-button',
                  id: 'reorder-remove-current',
                  commandId: reorderCommandIds.removeCurrent,
                  variant: 'text',
                  categories: ['reorder'],
                },
                {
                  type: 'divider',
                  id: 'reorder-tools-divider',
                  orientation: 'vertical',
                },
                {
                  type: 'command-button',
                  id: 'reorder-move-up',
                  commandId: reorderCommandIds.moveUp,
                  variant: 'text',
                  categories: ['reorder'],
                },
                {
                  type: 'command-button',
                  id: 'reorder-move-down',
                  commandId: reorderCommandIds.moveDown,
                  variant: 'text',
                  categories: ['reorder'],
                },
              ],
            },
            {
              type: 'spacer',
              id: 'reorder-spacer-end',
              flex: true,
            },
          ],
        };

        // Add "Reorder" tab to main-toolbar mode tabs and "Save and Close" button
        const mainToolbar = schema.toolbars?.['main-toolbar'];
        let mainToolbarPatch = {
        };
        if (mainToolbar && Array.isArray(mainToolbar.items)) {
          const items = JSON.parse(JSON.stringify(mainToolbar.items));
          const modeTabs = items.find((item) => item.id === 'mode-tabs' && item.tabs);
          if (modeTabs && Array.isArray(modeTabs.tabs)) {
            const tabs = [...modeTabs.tabs];
            const insertIndex = tabs.findIndex((t) => t.id === 'redact-mode');
            const newTab = {
              id: 'reorder-mode',
              commandId: 'mode:reorder',
              variant: 'text',
              categories: ['mode', 'mode-reorder'],
            };
            if (insertIndex >= 0) {
              tabs.splice(insertIndex + 1, 0, newTab);
            } else {
              tabs.push(newTab);
            }
            modeTabs.tabs = tabs;
          }

          const spacerIndex = items.findIndex((item) => item.id === 'spacer-1');
          const divider = {
            type: 'divider',
            id: 'divider-4',
            orientation: 'vertical',
          };
          if (spacerIndex !== -1) {
            items.splice(spacerIndex, 1, divider);
          }

          // Insert a divider between the spacer and the save and close button
          items.push({
            ...divider,
            id: 'divider-5',
          });

          items.push({
            type: 'command-button',
            id: 'save-and-close-btn',
            commandId: 'custom.save-and-close',
            variant: 'text',
          });
          mainToolbarPatch = {
            'main-toolbar': {
              ...mainToolbar,
              items,
            },
          };
        }

        ui.mergeSchema({
          toolbars: {
            'reorder-toolbar': reorderToolbar,
            ...mainToolbarPatch,
          },
        });

      }).catch(() => {
        // Registry may reject if element is removed before ready
      });
    },

    /** Move the current page one position later (index increases). */
    async movePageDown() {
      if (this.activePageIndex >= this.pageCount - 1 || this.pageCount <= 1) return;
      this.saving = true;
      this.errorMessage = '';
      try {
        const newOrder = [...Array(this.pageCount).keys()];
        const i = this.activePageIndex;
        [newOrder[i], newOrder[i + 1]] = [newOrder[i + 1], newOrder[i]];
        await this.applyReorder(newOrder, i + 1);
        this.activePageIndex = i + 1;
      } catch (err) {
        this.errorMessage = err.message || 'Failed to move page down';
        this.$emit('error', this.errorMessage);
      } finally {
        this.saving = false;
      }
    },

    /** Move the current page one position earlier (index decreases). */
    async movePageUp() {
      if (this.activePageIndex <= 0 || this.pageCount <= 1) return;
      this.saving = true;
      this.errorMessage = '';
      try {
        const newOrder = [...Array(this.pageCount).keys()];
        const i = this.activePageIndex;
        [newOrder[i - 1], newOrder[i]] = [newOrder[i], newOrder[i - 1]];
        await this.applyReorder(newOrder, i - 1);
        this.activePageIndex = i - 1;
      } catch (err) {
        this.errorMessage = err.message || 'Failed to move page up';
        this.$emit('error', this.errorMessage);
      } finally {
        this.saving = false;
      }
    },

    /**
     * Update the viewer with current bytes. If the viewer is already mounted and
     * document-manager is available, replaces the document in-place (no full re-mount).
     * Otherwise (initial load or no document-manager), mounts or re-mounts the viewer.
     * @param {Object} [options] - Optional restore state after replace.
     * @param {number} [options.targetPageNumber] - 1-based page number to scroll to after replace.
     */
    async refreshViewerFromBytes(options) {
      const opts = options ?? {
      };
      if (!this.pdfBytes || this.pdfBytes.length === 0) {
        return;
      }
      if (this.viewerElement && this._docManager) {
        const targetPageNumber = opts.targetPageNumber ?? (this.activePageIndex + 1);
        this._pendingScrollPageNumber = Math.max(1, Math.min(targetPageNumber, this.pageCount || 1));
        this._pendingSidebarState = this.captureSidebarOpenState();

        const buffer = this.pdfBytes.buffer.slice(
          this.pdfBytes.byteOffset,
          this.pdfBytes.byteOffset + this.pdfBytes.byteLength,
        );
        const previousId = this._currentDocumentId;
        try {
          if (previousId) {
            await this._docManager.openDocumentBuffer({
              buffer,
              name: 'document.pdf',
              autoActivate: true,
            });
            this._docManager.closeDocument(previousId);
          } else {
            this._docManager.closeAllDocuments();
            await this._docManager.openDocumentBuffer({
              buffer,
              name: 'document.pdf',
              autoActivate: true,
            });
          }
        } catch (err) {
          this._pendingScrollPageNumber = null;
          this._pendingSidebarState = null;
          console.warn('[AppPDFEditor] document-manager update failed, falling back to full refresh:', err);
          this.cleanupBlobUrl();
          const blob = new Blob([this.pdfBytes], {
            type: 'application/pdf',
          });
          this._currentBlobUrl = URL.createObjectURL(blob);
          await this.mountOrUpdateViewer(this._currentBlobUrl);
        }
        return;
      }
      this.cleanupBlobUrl();
      const blob = new Blob([this.pdfBytes], {
        type: 'application/pdf',
      });
      this._currentBlobUrl = URL.createObjectURL(blob);
      await this.mountOrUpdateViewer(this._currentBlobUrl);
    },

    /** Remove the current page. Guard: do not remove last remaining page. */
    async removeCurrentPage() {
      if (this.pageCount <= 1) {
        this.errorMessage = 'Cannot remove the last page.';
        this.$emit('error', this.errorMessage);
        return;
      }
      this.saving = true;
      this.errorMessage = '';
      try {
        await this.syncBytesFromViewer();
        const doc = await this.createWorkingDoc();
        doc.removePage(this.activePageIndex);
        this.pdfBytes = await doc.save();
        this.pageCount = doc.getPageCount();
        if (this.activePageIndex >= this.pageCount) {
          this.activePageIndex = Math.max(0, this.pageCount - 1);
        }
        await this.refreshViewerFromBytes();
      } catch (err) {
        this.errorMessage = err.message || 'Failed to remove page';
        this.$emit('error', this.errorMessage);
      } finally {
        this.saving = false;
      }
    },

    async saveAndClose() {
      this.spinner(true);
      this.saving = true;
      this.errorMessage = '';
      try {
        const bytes = await this.exportViewerBytes();
        if (!bytes || bytes.length === 0) {
          console.warn('[AppPDFEditor] No bytes to save, closing editor');
          this.closePDFEditor();
          return;
        }

        const { identityId, filename } = this.storageKey
          ? parseStorageKey(this.storageKey)
          : {
            identityId: this.$store.getters['UserProfile/identityId'],
            filename: this.filename || 'document.pdf',
          };

        const { data } = await API.getPreSignedURL({
          level: 'protected',
          identityId,
          attachmentId: this.attachmentId,
          filename,
        });
        const presignedUrl = data.getPreSignedURL;

        const blob = new Blob([bytes], {
          type: 'application/pdf',
        });

        const response = await fetch(presignedUrl, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/pdf',
          },
          body: blob,
        });
        if (!response.ok) {
          throw new Error(`S3 upload failed: ${response.status} ${response.statusText}`);
        }

        this.$emit('saved');
      } catch (err) {
        console.error('[AppPDFEditor] saveAndClose error:', err);
        this.errorMessage = err.message || 'Failed to save PDF';
        this.$emit('error', this.errorMessage);
      } finally {
        this.saving = false;
        this.spinner(false);
        this.closePDFEditor();
      }
    },

    /**
     * Sync this.pdfBytes from the live viewer state before any mutation so
     * that annotations added by the user are not silently discarded.
     */
    async syncBytesFromViewer() {
      const fresh = await this.exportViewerBytes();
      if (fresh && fresh.length > 0) {
        this.pdfBytes = fresh;
      }
    },

    /** Unsubscribe from scroll plugin page-change listener and clear ref. */
    unsubscribeScroll() {
      if (this.scrollUnsubscribe) {
        this.scrollUnsubscribe();
        this.scrollUnsubscribe = null;
      }
      this.scrollPlugin = null;
    },
  },
};
</script>

<style scoped lang="scss">
.app-pdf-editor {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  overflow: hidden;

  &__container {
    flex: 1;
    min-height: 0;
    overflow: hidden;
  }
}
</style>
